import translations from "@/config/translations";
import useLangStore from "@/hooks/useLangStore";
import usePopupStore from "@/hooks/usePopupStore";
import ActionMenuPopup from "@/UI/popups/ActionMenuPopup";
import GalleryPopup from "@/UI/popups/GalleryPopup";
import TextPopup from "@/UI/popups/TextPopup";
import { useRef, useState } from "react";
import "./styles.css";

// ── TextCell ──────────────────────────────────────────────────────────────────

interface TextCellProps {
    value: string;
    maxChars?: number;
    expandable?: boolean;
    direction?: string;
}

function TextCell({ value, maxChars = 55, expandable = true, direction }: TextCellProps) {
    const [expanded, setExpanded] = useState(false);
    const isTruncated = expandable && value && value.length > maxChars;

    const style = direction ? { direction: direction as "ltr" | "rtl", textAlign: "end" as any } : undefined;

    return (
        <div className="tc-text">
            <span style={style} className={`tc-text-value${expanded ? " tc-text-value--expanded" : ""}`}>
                {value}
            </span>
            {isTruncated && (
                <button
                    className="tc-text-expand-btn"
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded((v) => !v);
                    }}
                    aria-label={expanded ? "Collapse" : "Expand"}
                >
                    <img
                        src="/icons/chevron-down-icon.svg"
                        alt=""
                        width={16}
                        height={16}
                        className={`tc-text-expand-icon${expanded ? " tc-text-expand-icon--open" : ""}`}
                    />
                </button>
            )}
        </div>
    );
}

// ── DescriptionCell ───────────────────────────────────────────────────────────

interface DescriptionCellProps {
    value: string;
    title?: string;
    maxChars?: number;
}

function DescriptionCell({ value, title, maxChars = 55 }: DescriptionCellProps) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();
    const isTruncated = value && value.length > maxChars;

    const openPopup = (e: React.MouseEvent) => {
        e.stopPropagation();
        const name = "text-popup";
        removeComp(name);
        addComp({
            name,
            Component: TextPopup,
            details: { title: title ?? t.table.viewDetails, message: value },
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    return (
        <div className="tc-desc">
            <span className="tc-desc-value">{value}</span>
            {isTruncated && (
                <button className="tc-desc-expand-btn" type="button" onClick={openPopup} aria-label="View details">
                    <img src="/icons/chevron-down-icon.svg" alt="" width={16} height={16} />
                </button>
            )}
        </div>
    );
}

// ── NumberCell ────────────────────────────────────────────────────────────────

interface NumberCellProps {
    value: number | null | undefined;
    format?: "integer" | "decimal" | "percent";
}

function NumberCell({ value, format = "integer" }: NumberCellProps) {
    let display = "";
    if (value != null) {
        if (format === "decimal") display = value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        else if (format === "percent") display = `${value.toLocaleString()}%`;
        else display = value.toLocaleString();
    }

    return (
        <div className="tc-number">
            <span className="tc-number-value">{display}</span>
        </div>
    );
}

// ── ImageCell ─────────────────────────────────────────────────────────────────

interface ImageCellProps {
    src?: string;
    alt: string;
    size?: number;
    radius?: string;
}

function ImageCell({ src, alt, size = 40, radius = "var(--radius-lev4)" }: ImageCellProps) {
    const [broken, setBroken] = useState(false);
    const showFallback = !src || broken;

    return (
        <div className="tc-image" style={{ width: size, height: size, borderRadius: radius }}>
            {showFallback ? (
                <div className="tc-image-fallback">
                    <img src="/icons/client-small-placeholder-icon.svg" alt="" width={size * 0.5} height={size * 0.5} />
                </div>
            ) : (
                <img src={src} alt={alt} onError={() => setBroken(true)} />
            )}
        </div>
    );
}

// ── StatusCell ────────────────────────────────────────────────────────────────

interface StatusCellProps {
    active: boolean;
    onToggle?: () => void;
}

function StatusCell({ active, onToggle }: StatusCellProps) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];

    return (
        <span
            className={`tc-status tc-status--${active ? "active" : "inactive"}${onToggle ? " tc-status--clickable" : ""}`}
            onClick={
                onToggle
                    ? (e) => {
                          e.stopPropagation();
                          onToggle();
                      }
                    : undefined
            }
        >
            {active ? t.table.active : t.table.inactive}
        </span>
    );
}

// ── ActionsCell ───────────────────────────────────────────────────────────────

interface ActionItem {
    name: string;
    icon: string;
    handler: () => void;
    danger?: boolean;
}

interface ActionsCellProps {
    actions: ActionItem[];
    disabled?: boolean;
}

function ActionsCell({ actions, disabled }: ActionsCellProps) {
    const btnRef = useRef<HTMLButtonElement>(null);
    const { addComp, removeComp } = usePopupStore();

    const openMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        const rect = btnRef.current?.getBoundingClientRect();
        if (!rect) return;

        const name = "action-menu";
        removeComp(name);
        addComp({
            name,
            Component: ActionMenuPopup,
            details: { actions },
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            coverClose: true,
            anchor: {
                top: rect.top,
                left: rect.left,
                right: rect.right,
                bottom: rect.bottom,
                width: rect.width,
                height: rect.height,
            },
        });
    };

    return (
        <div className={`tc-actions${disabled ? " tc-actions--disabled" : ""}`}>
            <button ref={btnRef} type="button" className="tc-actions-btn" onClick={openMenu} disabled={disabled} aria-label="Actions">
                <img src="/icons/more-icon.svg" alt="" />
            </button>
        </div>
    );
}

// ── GalleryCell ───────────────────────────────────────────────────────────────

interface GalleryCellProps {
    images: string[];
    alt?: string;
    squared?: boolean;
}

function GalleryCell({ images, alt = "", squared = false }: GalleryCellProps) {
    const { addComp, removeComp } = usePopupStore();

    const valid = (images ?? []).filter(Boolean);
    const squaredClass = squared ? " tc-gallery--squared" : "";

    const openGallery = () => {
        if (valid.length === 0) return;
        const name = "gallery";
        removeComp(name);
        addComp({
            name,
            Component: GalleryPopup,
            details: { images: valid, alt },
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    if (valid.length === 0) {
        return (
            <div className={`tc-gallery-empty${squaredClass}`}>
                <img src="/icons/client-small-placeholder-icon.svg" alt="" width={20} height={20} />
            </div>
        );
    }

    if (valid.length === 1) {
        return (
            <div className={`tc-gallery tc-gallery--single${squaredClass}`} onClick={openGallery}>
                <div className="tc-gallery-card">
                    <img src={valid[0]} alt={alt} />
                </div>
            </div>
        );
    }

    const count = Math.min(valid.length, 3);

    return (
        <div className={`tc-gallery${squaredClass}`} onClick={openGallery}>
            <div className="tc-gallery-stack">
                {/* Back — least important image, no rotation */}
                <div className="tc-gallery-slot tc-gallery-slot--back">
                    <div className="tc-gallery-card">
                        <img src={valid[count - 1]} alt={alt} />
                    </div>
                </div>
                {/* Middle — only when 3 images available */}
                {count >= 3 && (
                    <div className="tc-gallery-slot tc-gallery-slot--mid">
                        <div className="tc-gallery-card tc-gallery-card--mid">
                            <img src={valid[1]} alt={alt} />
                        </div>
                    </div>
                )}
                {/* Front — primary image, most visible */}
                <div className="tc-gallery-slot tc-gallery-slot--front">
                    <div className="tc-gallery-card tc-gallery-card--front">
                        <img src={valid[0]} alt={alt} />
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── BadgeCell ─────────────────────────────────────────────────────────────────

interface BadgeCellProps {
    label: string;
    variant?: "orange" | "green" | "red" | "gray";
}

function BadgeCell({ label, variant = "gray" }: BadgeCellProps) {
    return <span className={`tc-badge tc-badge--${variant}`}>{label}</span>;
}

// ── Namespace export ──────────────────────────────────────────────────────────

const TableCell = {
    Text: TextCell,
    Description: DescriptionCell,
    Number: NumberCell,
    Image: ImageCell,
    Status: StatusCell,
    Actions: ActionsCell,
    Badge: BadgeCell,
    Gallery: GalleryCell,
};

export default TableCell;
